import { useDebounceFn } from '@vueuse/core';
import { isObjectsEqual } from '~/utils/isObjectsEqual';

type HandledEvent = { isHandled: boolean };

const userDecorationsPushThrottle = 1000 * 60 * 60;
const haveSeenOn = ref(false);
const signupStage = ref('');
const signupPage = ref('');
const signupPlan = ref('');
const signupDt = ref('');
const signupNumAttempts = ref('');
const planGroup = ref('');

/** Create an HekateClickEvent for GA */
const onClickBody = (evt: MouseEvent) => {
  if (!evt.target || !(evt.target instanceof Element)) {
    return;
  }

  if ((evt as unknown as HandledEvent).isHandled) {
    return;
  }

  const elm = evt.target;
  const data: { [key: string]: string | null } = {};
  const attributePrefix = 'clk-dec-';
  let href = '';
  let p = elm;
  while (p.parentElement) {
    const cur = p;
    if (!href) {
      href = cur.getAttribute('href') || '';
    }

    cur
      .getAttributeNames()
      .filter((name) => name.startsWith(attributePrefix))
      .forEach((name) => {
        const val = cur.getAttribute(name);
        if (!val) return;
        const key = `clk-dec-${name.substring(attributePrefix.length)}`;
        data[key] = val;
      });
    p = p.parentElement;
  }

  if (!href) {
    return;
  }
  // set the evt has "handled" to avoid double pushing
  (evt as unknown as HandledEvent).isHandled = true;

  let { textContent } = elm;
  ['alt', 'title', 'class'].forEach((attrName) => {
    if (textContent) return;
    const val = elm.getAttribute(attrName);
    if (!val) return;
    textContent = `${attrName}: ${val}`;
  });
  textContent = textContent || '';
  textContent = textContent.replace(/\s+/g, ' ');
  textContent = textContent.length <= 140 ? textContent : `${textContent.substring(0, 140)}...`;
  textContent = textContent && textContent.trim() ? textContent.trim() : 'unknown';

  // If you add more decorations you MUST contact whoever is managing GoogleTagManager
  const toPush = {
    ...data,
    'clk-dec-href': href,
    'clk-dec-note': textContent,
  };

  dataLayerPush({
    event: 'HekateLinkClick',
    ...toPush,
  });
};

/** init click tracking */
const initClickTracking = () => {
  if (import.meta.server || !window || !document.body) {
    return;
  }
  document.body.addEventListener('click', onClickBody);
};

/** wrapper around window._mfq.push() */
const mouseFlowPush = (toPush: (string | boolean)[]): void => {
  if (import.meta.server || !window || !window._mfq) return;
  window._mfq = window._mfq || [];
  window._mfq.push(toPush);
};

/** safely wrappers around window.dataLayer */
const dataLayerPush = (data: { [key: string]: string; event: string }) => {
  if (import.meta.server || !window || !window.wwwGaIsReadyPromise) return;

  window.wwwGaIsReadyPromise.then(() => {
    window.ptcDebug = window.ptcDebug || {};
    window.ptcDebug.dataLayerPushes = window.ptcDebug.dataLayerPushes || [];
    window.ptcDebug.dataLayerPushes.push(data);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  });
};

const cookieUpdates = () => {
  const { isDeveloper } = useEnvContext();
  const { personalizationProfileStorable, getCookieStringByName } = useWwwStorage();

  if (isDeveloper) {
    mouseFlowPush(['tag', 'webdev']);
  }

  const cookieHaveSeenOn = !!getCookieStringByName('x-www-have-seen-on');
  if (cookieHaveSeenOn && cookieHaveSeenOn !== haveSeenOn.value) {
    mouseFlowPush(['tag', 'onshape-user']);
  }
  haveSeenOn.value = cookieHaveSeenOn;

  const cookiePlanGroup = getCookieStringByName('x-www-plan-group')?.value;
  if (cookiePlanGroup && cookiePlanGroup !== planGroup.value) {
    mouseFlowPush(['setVariable', 'plan-group', planGroup.value, 'session', true]);
  }
  planGroup.value = `${cookiePlanGroup}`;

  // See if we should clear 'signup-stage' data
  if (
    haveSeenOn &&
    personalizationProfileStorable.value?.signupDecoration &&
    personalizationProfileStorable.value?.signupDecoration.stage !== 'have-logged-in'
  ) {
    personalizationProfileStorable.value.signupDecoration.stage = 'have-logged-in';
    mouseFlowPush(['setVariable', 'signup-stage', 'have-logged-in', 'session', false]);
    personalizationProfileStorable.value.signupDecoration = undefined;
  }

  if (personalizationProfileStorable.value?.signupDecoration) {
    signupStage.value = personalizationProfileStorable.value.signupDecoration.stage;
    signupPage.value = `${personalizationProfileStorable.value.signupDecoration.page}`;
    signupPlan.value = personalizationProfileStorable.value.signupDecoration.planId;
    const signupDecorationDateAdded =
      personalizationProfileStorable.value.signupDecoration.dateAdded || 0;
    signupDt.value = ((Date.now() - signupDecorationDateAdded) / (1000 * 60 * 60)).toFixed(1);
    signupNumAttempts.value = `${personalizationProfileStorable.value.signupDecoration.numAttempts}`;

    mouseFlowPush(['setVariable', 'signup-stage-dt', signupDt.value, 'session', false]);
    mouseFlowPush(['setVariable', 'signup-stage', signupStage.value, 'session', false]);

    if (parseInt(signupNumAttempts.value) > 1) {
      mouseFlowPush([
        'setVariable',
        'signup-stage-num-attempts',
        `${signupNumAttempts}`,
        'session',
        false,
      ]);
    }
  }
};

const crossSessionReadyDecorations = () => {
  const { isDeveloper } = useEnvContext();
  const { isLoggedIn } = useCrossSession();
  const { personalizationProfileStorable } = useWwwStorage();

  const loggedIn = isLoggedIn ? 'yes' : 'no';
  mouseFlowPush(['setVariable', 'logged-in', loggedIn]);
  cookieUpdates();

  // If you add more decorations you MUST contact whoever is managing GoogleTagManager
  const decorations: { [key: string]: boolean | string | null | number } = {
    'usr-dec-is-developer': `${isDeveloper}`,
    'usr-dec-is-onshape-user': haveSeenOn.value,
    'usr-dec-is-logged-in': !!isLoggedIn,
    'usr-dec-plan-group':
      personalizationProfileStorable.value?.currentUserData.activePlanGroup || '',
    'usr-dec-product-sku': personalizationProfileStorable.value?.currentUserData.activePlanId || '',
    'usr-dec-signup-stage': signupStage.value || '',
    'usr-dec-signup-stage-page': signupPage.value || '',
    'usr-dec-signup-stage-plan': signupPlan.value || '',
    'usr-dec-signup-stage-dt': signupDt.value || '',
    'usr-dec-signup-stage-num-attempts': signupNumAttempts.value || '',
  };

  // remove any undefined or empty values
  const filtered = Object.fromEntries(
    Object.entries(decorations).filter(
      ([, vv]) => typeof vv !== 'undefined' && vv?.toString().trim()
    )
  );

  const lastPushedDecorationsDate =
    personalizationProfileStorable.value?.lastPushedDecorationsDateAdded || 0;
  const dt = Date.now() - lastPushedDecorationsDate;
  const lastPushedDecorations = personalizationProfileStorable.value?.lastPushedDecorations || {};

  if (dt < userDecorationsPushThrottle && isObjectsEqual(filtered, lastPushedDecorations)) {
    console.log('user decorations throttled, nothing new');
    return;
  }

  dataLayerPush({
    event: 'HekateUserDecoration',
    ...filtered,
  });

  if (personalizationProfileStorable.value) {
    personalizationProfileStorable.value.lastPushedDecorations = filtered;
    personalizationProfileStorable.value.lastPushedDecorationsDateAdded = Date.now();
  }
};

/** Update decorations when isUserInit changes */
const watchInitUserCallback = useDebounceFn(() => {
  crossSessionReadyDecorations();
}, 500);

/** Utils for interacting with data layer objects */
export const useTracking = () => {
  const { isUserInit } = useCrossSession();

  watch(isUserInit, watchInitUserCallback);

  return { mouseFlowPush, dataLayerPush, initClickTracking };
};
